import {
  PbActionConfig,
  PbGetRDVSurplusV2Response,
  PbGetRefundConfigurationV2Response,
  PbGetReturnProcessConditionsV2Response,
  PbGetReturnReasonsV2Response,
  PbKeepYourItem,
  PbPaymentMethodConfig,
  PbRDVSurplusRule,
  PbRefundConfigurationV2,
  PbReturnPeriodRule,
  PbReturnProcessConditions,
  PbSaveReturnProcessConditionsReq,
  PbSaveReturnProcessConditionsReqReviewAutoCompleteOutcomeEnum as Outcome,
  PbSaveReturnProcessConditionsReqStartReturnOnPlatformTimeEnum as StartTime
} from '@itsrever/rever-api'
import { Layered } from '@/domain/group-settings'
import {
  GroupReturnConditions,
  KeepYourItemRule,
  Requirement,
  ReturnPeriodRule,
  ReviewAutoComplete,
  START_RETURN_ON_PLATFORM_TIMES
} from '@/domain/return-conditions'
import {
  OutcomeRule,
  RDVSurplusRule,
  RefundConfiguration
} from '@/domain/refund'
import { ReturnReason } from '@/domain/return-reasons'
import {
  toBackendReasons,
  toReturnReasons
} from '@/infrastructure/api/return-reasons'

function toLayered<P, T>(
  group: P | undefined,
  overrides: { ecommerce_id?: string; value?: P }[],
  map: (value: P) => T
): Layered<T> {
  return {
    group: group ? map(group) : undefined,
    overrides: Object.fromEntries(
      overrides
        .filter((o) => o.ecommerce_id && o.value)
        .map((o) => [o.ecommerce_id as string, map(o.value as P)])
    )
  }
}

export const toLayeredReturnConditions = (
  response: PbGetReturnProcessConditionsV2Response
): Layered<GroupReturnConditions> =>
  toLayered(
    response.conditions,
    (response.overrides ?? []).map((o) => ({
      ecommerce_id: o.ecommerce_id,
      value: o.conditions
    })),
    toGroupReturnConditions
  )

export const toLayeredRefundConfiguration = (
  response: PbGetRefundConfigurationV2Response
): Layered<RefundConfiguration> =>
  toLayered(
    response.refund_configuration,
    (response.overrides ?? []).map((o) => ({
      ecommerce_id: o.ecommerce_id,
      value: o.refund_configuration
    })),
    toRefundConfiguration
  )

export const toLayeredRDVSurplus = (
  response: PbGetRDVSurplusV2Response
): Layered<RDVSurplusRule[]> =>
  toLayered(
    response.rdv_surplus,
    (response.overrides ?? []).map((o) => ({
      ecommerce_id: o.ecommerce_id,
      value: o.rdv_surplus
    })),
    (surplus) => (surplus.rules ?? []).map(toRDVSurplusRule)
  )

// The returns portal applies the fixed amount when a rule has both, so that is the one kept.
// A proto float32 decodes as e.g. 0.10000000149011612; seven significant digits is all it holds.
const toRDVSurplusRule = (rule: PbRDVSurplusRule): RDVSurplusRule => {
  const amountCents = Number(rule.surplus_amount ?? 0)
  const percentage = Number((rule.surplus_percentage ?? 0).toPrecision(7))
  return amountCents === 0 && percentage > 0
    ? { type: 'PERCENTAGE', value: percentage, whens: rule.whens ?? [] }
    : { type: 'FIXED', value: amountCents, whens: rule.whens ?? [] }
}

export const toPbRDVSurplusRules = (
  rules: RDVSurplusRule[]
): PbRDVSurplusRule[] =>
  rules.map((rule) => ({
    surplus_amount: String(rule.type === 'FIXED' ? rule.value : 0),
    surplus_percentage: rule.type === 'PERCENTAGE' ? rule.value : 0,
    whens: rule.whens
  }))

export const toLayeredReturnReasons = (
  response: PbGetReturnReasonsV2Response
): Layered<ReturnReason[]> =>
  toLayered(
    response.return_reasons,
    (response.overrides ?? []).map((o) => ({
      ecommerce_id: o.ecommerce_id,
      value: o.return_reasons
    })),
    toReturnReasons
  )

// 0 days is off whatever the outcome says; an unset outcome behaves as missing.
function reviewAutoComplete(
  conditions: PbReturnProcessConditions
): ReviewAutoComplete {
  if (!conditions.review_auto_complete_days) return 'OFF'
  return conditions.review_auto_complete_outcome === Outcome.Approved
    ? 'APPROVED'
    : 'MISSING'
}

const toRequirement = (requirement?: {
  always?: boolean
  options?: { whens?: string[] }[]
}): Requirement => ({
  always: requirement?.always ?? false,
  rules: (requirement?.options ?? []).map((o) => ({ whens: o.whens ?? [] }))
})

const toKeepYourItemRule = (
  option: NonNullable<PbKeepYourItem['options']>[number]
): KeepYourItemRule => ({
  whens: option.whens ?? [],
  refundPercentage: option.refund_percentage ?? 0,
  force: option.force_keep_your_item ?? false
})

const toReturnPeriodRule = (rule: PbReturnPeriodRule): ReturnPeriodRule => ({
  start: rule.purchased_from ?? '',
  end: rule.purchased_to ?? '',
  deadline: rule.until
    ? { kind: 'until', date: rule.until, days: null }
    : { kind: 'days', date: '', days: rule.days ?? null }
})

const toPbReturnPeriodRule = ({
  start,
  end,
  deadline
}: ReturnPeriodRule): PbReturnPeriodRule =>
  deadline.kind === 'until'
    ? { purchased_from: start, purchased_to: end, until: deadline.date }
    : { purchased_from: start, purchased_to: end, days: deadline.days ?? 0 }

export function toGroupReturnConditions(
  c: PbReturnProcessConditions
): GroupReturnConditions {
  return {
    returnPeriodDays: c.return_period_days ?? 0,
    returnPeriodRules: (c.return_period_rules ?? []).map(toReturnPeriodRule),
    maxDaysToSendPackage: c.max_days_to_send_package ?? 0,
    reviewFlow: c.review_flow ?? '',
    reviewAutoComplete: reviewAutoComplete(c),
    reviewAutoCompleteDays: c.review_auto_complete_days ?? 0,
    startReturnOnPlatformTime:
      c.start_return_on_platform_time ?? START_RETURN_ON_PLATFORM_TIMES[0],
    skipFraudDetection: c.skip_fraud_detection ?? false,
    paymentsEnabled: c.payments_enabled ?? false,
    randomizeReturnReasonsOrder: c.randomize_return_reasons_order ?? false,
    markUnreviewedItemsAsMissing: false,
    mandatoryReturnComment: c.mandatory_return_comment ?? false,
    nonReturnable: {
      productIds: c.non_returnable_product_ids ?? [],
      categoryIds: c.non_returnable_category_ids ?? [],
      brandIds: c.non_returnable_brand_ids ?? [],
      collectionIds: c.non_returnable_collection_ids ?? [],
      tagIds: c.non_returnable_tag_ids ?? [],
      orderTagIds: c.non_returnable_order_tag_ids ?? []
    },
    payForLogistics: toRequirement(c.pay_for_logistics),
    requireAuthorization: toRequirement(c.require_authorization),
    uploadDocuments: toRequirement(c.upload_documents),
    keepYourItem: {
      enabled: c.keep_your_item?.enabled ?? false,
      rules: (c.keep_your_item?.options ?? []).map(toKeepYourItemRule)
    }
  }
}

const toPbRequirement = (requirement: Requirement) => ({
  always: requirement.always,
  options: requirement.rules.map((rule) => ({ whens: rule.whens }))
})

export function toPbReturnConditions(
  c: GroupReturnConditions
): PbSaveReturnProcessConditionsReq {
  const autoComplete = c.reviewAutoComplete !== 'OFF'
  return {
    return_period_days: c.returnPeriodDays,
    return_period_rules: c.returnPeriodRules.map(toPbReturnPeriodRule),
    max_days_to_send_package: c.maxDaysToSendPackage,
    review_flow: c.reviewFlow,
    review_auto_complete_days: autoComplete ? c.reviewAutoCompleteDays : 0,
    review_auto_complete_outcome: !autoComplete
      ? Outcome.Unspecified
      : c.reviewAutoComplete === 'APPROVED'
        ? Outcome.Approved
        : Outcome.Missing,
    start_return_on_platform_time:
      c.startReturnOnPlatformTime as (typeof StartTime)[keyof typeof StartTime],
    skip_fraud_detection: c.skipFraudDetection,
    payments_enabled: c.paymentsEnabled,
    randomize_return_reasons_order: c.randomizeReturnReasonsOrder,
    mandatory_return_comment: c.mandatoryReturnComment,
    non_returnable_product_ids: c.nonReturnable.productIds,
    non_returnable_category_ids: c.nonReturnable.categoryIds,
    non_returnable_brand_ids: c.nonReturnable.brandIds,
    non_returnable_collection_ids: c.nonReturnable.collectionIds,
    non_returnable_tag_ids: c.nonReturnable.tagIds,
    non_returnable_order_tag_ids: c.nonReturnable.orderTagIds,
    pay_for_logistics: toPbRequirement(c.payForLogistics),
    require_authorization: toPbRequirement(c.requireAuthorization),
    upload_documents: toPbRequirement(c.uploadDocuments),
    keep_your_item: {
      enabled: c.keepYourItem.enabled,
      options: c.keepYourItem.rules.map((rule) => ({
        whens: rule.whens,
        refund_percentage: rule.refundPercentage,
        force_keep_your_item: rule.force
      }))
    }
  }
}

const toOutcomeRule = (
  config: PbPaymentMethodConfig | PbActionConfig
): OutcomeRule => ({
  target: config.name ?? '',
  timing: config.timing ?? '',
  whens: config.whens ?? []
})

export const toRefundConfiguration = (
  r: PbRefundConfigurationV2
): RefundConfiguration => ({
  methods: (r.payment_method_configs ?? []).map(toOutcomeRule),
  actions: (r.action_configs ?? []).map(toOutcomeRule),
  roundPromocode: r.round_promocode ?? false,
  revokeConditionalDiscounts: r.revoke_conditional_discounts ?? false
})

const toPbOutcome = (rule: OutcomeRule) => ({
  name: rule.target,
  timing: rule.timing,
  whens: rule.whens
})

export const toPbRefundConfiguration = (
  r: RefundConfiguration
): PbRefundConfigurationV2 => ({
  payment_method_configs: r.methods.map(toPbOutcome),
  action_configs: r.actions.map(toPbOutcome),
  round_promocode: r.roundPromocode,
  revoke_conditional_discounts: r.revokeConditionalDiscounts
})

export const toPbReturnReasons = toBackendReasons
